import React from 'react';
import { useUnderwriterContext } from '../../../provider/UnderwriterProvider';
import {
  ReferralStrategyForEdit,
  ReferralStrategyVariant,
} from '../../../types';
import {
  computeCategoryDisplayValue,
  createCategoryUpdates,
  getReferralStrategyOptions,
} from '../utils/referralStrategyDropdown.utils';

function getReferralStrategyFromContext(
  variant:
    | ReferralStrategyVariant.CATEGORY_COVERAGE
    | ReferralStrategyVariant.CATEGORY_RATING_OPTION,
  key: string,
  context: ReturnType<typeof useUnderwriterContext>
): ReferralStrategyForEdit {
  if (variant === ReferralStrategyVariant.CATEGORY_COVERAGE) {
    return (
      context.state.coverageState[key]?.referralStrategyForEdit ??
      ReferralStrategyForEdit.NONE
    );
  }

  if (variant === ReferralStrategyVariant.CATEGORY_RATING_OPTION) {
    return (
      context.state.ratingOptions[key]?.referralStrategyForEdit ??
      ReferralStrategyForEdit.NONE
    );
  }

  return ReferralStrategyForEdit.NONE;
}

export function useCategoryReferralStrategy(
  variant:
    | ReferralStrategyVariant.CATEGORY_COVERAGE
    | ReferralStrategyVariant.CATEGORY_RATING_OPTION,
  keys: string[]
) {
  const context = useUnderwriterContext();

  const getReferralStrategyForKey = React.useCallback(
    (key: string): ReferralStrategyForEdit => {
      return getReferralStrategyFromContext(variant, key, context);
    },
    [variant, context]
  );

  const displayValue = React.useMemo(() => {
    if (keys.length === 0) {
      return ReferralStrategyForEdit.NONE;
    }

    const strategies: ReferralStrategyForEdit[] = keys.map(
      getReferralStrategyForKey
    );
    return computeCategoryDisplayValue(strategies);
  }, [keys, getReferralStrategyForKey]);

  const options = React.useMemo(() => {
    return getReferralStrategyOptions(displayValue);
  }, [displayValue]);

  const handleStrategyChange = React.useCallback(
    (newStrategy: ReferralStrategyForEdit) => {
      const updates = createCategoryUpdates(keys, newStrategy);

      if (variant === ReferralStrategyVariant.CATEGORY_COVERAGE) {
        context.handlers.setBulkCoverages({ updates });
        return;
      }

      if (variant === ReferralStrategyVariant.CATEGORY_RATING_OPTION) {
        context.handlers.setRatingOption(updates);
      }
    },
    [variant, keys, context.handlers]
  );

  return {
    displayValue,
    options,
    handleStrategyChange,
  };
}
